import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Boxes, KeyRound, GraduationCap, Shield } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const DEMOS = [
  { role: "Student", email: "student@growthink.ai", icon: GraduationCap, color: "bg-primary" },
  { role: "Teacher", email: "teacher@growthink.ai", icon: KeyRound, color: "bg-accent" },
  { role: "Administrator", email: "admin@growthink.ai", icon: Shield, color: "bg-foreground" },
];

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const doLogin = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    setBusy(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(
        user.role === "student"
          ? "/app/student"
          : user.role === "teacher"
          ? "/app/teacher"
          : "/app/admin"
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (demoEmail) => {
    setEmail(demoEmail);
    setPassword("Demo@123");
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-12 bg-background">
      {/* Left — branding */}
      <div className="relative hidden border-r border-foreground bg-foreground p-10 text-background lg:col-span-5 lg:flex lg:flex-col lg:justify-between bg-grid-dense">
        <Link to="/" className="flex items-center gap-2.5" data-testid="login-logo-link">
          <div className="flex h-9 w-9 items-center justify-center bg-background text-foreground">
            <Boxes className="h-5 w-5" strokeWidth={2.5} />
          </div>
          <div className="font-display text-lg font-extrabold tracking-tighter">
            GROWTHINK · AI-CADEMY
          </div>
        </Link>

        <div className="max-w-md">
          <div className="mono mb-3 text-[10px] uppercase tracking-widest text-background/60">
            /sign in
          </div>
          <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tighter sm:text-5xl">
            Your lifelong learning identity — anchored on-chain.
          </h1>
          <p className="mt-5 text-sm leading-relaxed text-background/70">
            Sign in to access your academic wallet, courses, blockchain
            certificates and AI tutor. Three demo personas are available for
            this preview.
          </p>
        </div>

        <div className="mono text-[10px] uppercase tracking-widest text-background/40">
          © 2026 · Pan-African Academic Blockchain Network
        </div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center p-6 lg:col-span-7">
        <div className="w-full max-w-md">
          <div className="mono mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            Authenticate
          </div>
          <h2 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">
            Sign in to your dashboard
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Use one of the demo accounts below — all passwords:{" "}
            <span className="mono font-semibold text-foreground">Demo@123</span>
          </p>

          <form onSubmit={doLogin} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="mono text-[10px] uppercase tracking-widest">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="student@growthink.ai"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none border-foreground"
                data-testid="login-email-input"
                autoComplete="username"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="mono text-[10px] uppercase tracking-widest">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Demo@123"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none border-foreground"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="w-full rounded-none bg-foreground text-background hard-shadow hover-lift"
              data-testid="login-submit-btn"
            >
              {busy ? "Verifying…" : "Sign in →"}
            </Button>
          </form>

          <div className="mt-8">
            <div className="mono mb-3 text-[10px] uppercase tracking-widest text-muted-foreground">
              One-click demo login
            </div>
            <div className="space-y-2">
              {DEMOS.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => fillDemo(d.email)}
                  className="flex w-full items-center gap-3 border border-foreground bg-background p-3 text-left transition-all hover-lift"
                  data-testid={`demo-${d.role.toLowerCase()}-btn`}
                >
                  <div
                    className={`${d.color} flex h-9 w-9 items-center justify-center text-background`}
                  >
                    <d.icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{d.role}</div>
                    <div className="mono text-[10px] text-muted-foreground">
                      {d.email}
                    </div>
                  </div>
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Fill →
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
